import React, { useState, useEffect, useCallback } from 'react';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { InputCommon } from 'components/common';
import { QueryParams } from 'types';
import { omit } from 'utils';

interface SearchByFieldsProps {
  filteredQuery: QueryParams;
  setFilteredQuery: (query: QueryParams) => void;
  searchFields: string[];
  placeholder?: string;
  debounceTime?: number;
  className?: string;
}

const SearchByFields: React.FC<SearchByFieldsProps> = ({
  filteredQuery,
  setFilteredQuery,
  searchFields,
  placeholder = 'Tìm kiếm...',
  debounceTime = 500,
  className = '',
}) => {
  const [searchText, setSearchText] = useState<string>('');
  useEffect(() => {
    if (filteredQuery.searchValue) {
      setSearchText(String(filteredQuery.searchValue));
    }
  }, [filteredQuery.searchValue]);

  const searchKeysString = searchFields.join('|');

  // Debounced search function
  const debouncedSearch = useCallback(
    (value: string) => {
      const timer = setTimeout(() => {
        if (value.trim()) {
          setFilteredQuery({
            ...filteredQuery,
            searchKey: searchKeysString,
            searchValue: value,
            page: 1, // Reset to first page when searching
          } as unknown as QueryParams);
        } else {
          // Remove search parameters if search is cleared
          const rest = omit(filteredQuery, ['searchKey', 'searchValue']);
          setFilteredQuery({
            ...rest,
            page: 1,
          } as unknown as QueryParams);
        }
      }, debounceTime);

      return () => clearTimeout(timer);
    },
    [filteredQuery, searchKeysString, setFilteredQuery, debounceTime],
  );

  // Handle input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchText('');
    const rest = omit(filteredQuery, ['searchKey', 'searchValue']);
    setFilteredQuery({
      ...rest,
      page: 1,
    } as unknown as QueryParams);
  };

  return (
    <InputCommon
      type='text'
      placeholder={placeholder}
      value={searchText}
      onChange={handleSearchChange}
      suffix={
        <MagnifyingGlass
          size={20}
          weight='bold'
          className='pl-[12px] w-[32px] border-l border-[var(--textBlack12)] cursor-pointer text-[var(--textBlack56)] hover:text-[var(--textBlack100)]'
          onClick={() => debouncedSearch(searchText)}
        />
      }
      className={`!h-[40px] !bg-[var(--textBlack4)] min-w-[200px] ${className}`}
      allowClear={!!searchText}
      onPressEnter={() => debouncedSearch(searchText)}
      onClear={handleClearSearch}
    />
  );
};

export default SearchByFields;
